import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import { Chip, Menu, MenuItem, Stack, Avatar, Badge } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import ButtonBase from "@mui/material/ButtonBase";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ListAltIcon from "@mui/icons-material/ListAlt";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BookIcon from "@mui/icons-material/Book";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StarIcon from "@mui/icons-material/Star";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { useContext } from "react";
import { UserDetailsContext } from "../Context/userDetailsContext";
import { useNavigation } from "../Context/NavigationContext";
import PersonIcon from "@mui/icons-material/Person";
import SettingsMenu from "./SettingsMenu";
import UserAvatarMenu from "./UserAvatarMenu";
import { getAvatarInitials, getAvatarColor } from "../utils/avatarUtils";
import { ConfigLinksDetailsContext } from "../Context/cofingLinksDetailsContext";
import AccessibleIcon from "@mui/icons-material/Accessible";
import MailIcon from "@mui/icons-material/Mail";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import i18n from "i18next";
import { useTranslation } from 'react-i18next';
import axios from "../axiosConfig";
// WhatsApp FAB removed per request

const buildPages = (isAuthenticated, unreadCount = 0, t, newsCatId = 0) => {
  return [
    {
      title: t('nav_home'),
      path: "/",
      icon: <HomeIcon />,
    },
    !isAuthenticated && {
        title: t('nav_guestServices', 'تقديم الطلب كزائر'),
        path: '/guest-services',
        icon: <AddCircleIcon />,
      },
    !isAuthenticated && {
      title: t('nav_trackRequest', 'تتبّع طلبك'),
      path: '/guest-track-request',
      icon: <TrackChangesIcon />,
    },
      isAuthenticated && {
      title: t('nav_submitRequest'),
      path: "/services",
      icon: <AddCircleIcon />,
    },
    isAuthenticated && {
      title: t('nav_myOrders'),
      path: "/my-orders",
      icon: <ListAltIcon />,
    },
    isAuthenticated && {
      title: t('nav_notifications'),
      path: "/my-notifications",
      icon: (
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      ),
      badgeContent: unreadCount,
    },
    isAuthenticated && {
      title: t('nav_myServices'),
      path: "/my-services",
      icon: <AssignmentIcon />,
    },
    isAuthenticated && {
      title: t('nav_rateApp'),
      path: "/rate-portal",
      icon: <StarIcon />,
    },
    {
      title: t('nav_servicesGuide'),
      path: "/services-guide",
      icon: <BookIcon />,
    },

    newsCatId > 0 && {
      title: t('nav_news', 'الأخبار'),
      path: "/news",
      icon: <NewspaperIcon />,
    },
  ].filter(Boolean);
};

function ResponsiveNavBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const { userDetails, setUserDetails } = useContext(UserDetailsContext);
  const { setIsMobileNavOpen } = useNavigation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [avatarAnchorEl, setAvatarAnchorEl] = React.useState(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = React.useState(0);
  const open = Boolean(anchorEl);
  const avatarMenuOpen = Boolean(avatarAnchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  // Municipality names from Emun.Api settings via configLinks
  const { configLinks } = React.useContext(ConfigLinksDetailsContext);
  const logoSrc = configLinks?.logoUrl ? `/${configLinks.logoUrl}` : "/attachments/Logos/logo.png";
  const municipalityNameAr = configLinks?.municipalityNameAr?.trim() || "";
  const municipalityNameEn = configLinks?.municipalityNameEn?.trim() || "";
  const titleAr = municipalityNameAr ? `${municipalityNameAr} - خدمات إلكترونية` : 'البلدية الإلكترونية';
  const titleEn = municipalityNameEn ? `${municipalityNameEn} - E-Services` : 'E-Municipality';

  // Fetch unread notification count when user is authenticated
  React.useEffect(() => {
    const fetchUnreadCount = async () => {
      if (userDetails.isAuthenticated) {
        try {
          const response = await axios.get("/user/notifications/unread-count");
          // Handle different response structures
          let count = 0;
          if (typeof response === 'number') {
            count = response;
          } else if (response?.unreadCount !== undefined) {
            count = response.unreadCount;
          } else if (response?.UnreadCount !== undefined) {
            count = response.UnreadCount;
          }
          setUnreadNotificationCount(count);
        } catch (error) {
          console.error("Error fetching unread count:", error);
          setUnreadNotificationCount(0);
        }
      } else {
        setUnreadNotificationCount(0);
      }
    };
    fetchUnreadCount();
  }, [userDetails.isAuthenticated, location.pathname]);

  // Sync drawer state with context
  React.useEffect(() => {
    setIsMobileNavOpen(drawerOpen);
  }, [drawerOpen, setIsMobileNavOpen]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAvatarClick = (event) => {
    setAvatarAnchorEl(event.currentTarget);
  };
  const handleAvatarClose = () => {
    setAvatarAnchorEl(null);
  };
  
  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const { t } = useTranslation();

  // Language state
  const normalizeLang = (lng) => (lng && lng.startsWith('ar') ? 'ar' : 'en');
  const [currentLang, setCurrentLang] = React.useState(normalizeLang(i18n.language));
  const title = currentLang === 'ar' ? titleAr : titleEn;

  React.useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(normalizeLang(lng));
      // Update document direction
      document.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Open Accessibility toolbar by simulating the Ctrl+F2 shortcut used there
  const openAccessibilityToolbar = () => {
    const ev = new KeyboardEvent("keydown", { ctrlKey: true, key: "F2" });
    window.dispatchEvent(ev);
  };

  const toggleLanguage = () => {
    const newLang = currentLang === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    localStorage.setItem("i18nextLng", newLang);
    setCurrentLang(newLang);
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const onPageClick = (path) => {
    handleCloseNavMenu(null);
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
    <AppBar
      sx={{
        padding: 0,
        backgroundColor: "white!important",
        boxShadow: { xs: "none", md: "none" },
        borderBottom: { xs: "1px solid #eaecef", md: "none" },
        borderRadius: 0,
      }}
      position="static"
    >
      <Container sx={{ padding: { xs: 0, md: 0 } }} maxWidth="xl">
        <ButtonBase
          onClick={() => navigate("/")}
          sx={{
            width: "100%",
            display: { xs: "none", md: "flex" },
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 0,
            px: 1,
            py: 0.5,
          }}
        >
        
          <img height={100} src={logoSrc} alt="logo" />
          {/* Replace single-line title with two-line Arabic/English title */}
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <Typography sx={{ fontWeight: "bold" }} color="primary" variant="h5">
              {title}
            </Typography>
            {/* <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }} color="primary">
              {titleEn}
            </Typography> */}
          </Box>
        </ButtonBase>
        
        <Toolbar disableGutters sx={{ px: { xs: 1, md: 0 }, minHeight: { xs: 70, md: 64 } }}>
          <Stack
            sx={{ width: "100%" }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
           
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1 }}>
                        <IconButton
                color="primary"
                aria-label="فتح القائمة"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 0.5,
                  cursor: "pointer",
                }}
              >
        
                <img height={60} src={logoSrc} alt="logo" />
                {/* Mobile: two-line title */}
                <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
                  <Typography sx={{ fontWeight: "bold", fontSize: "1.20rem" }} color="primary">
                    {title}
                  </Typography>
                  {/* <Typography sx={{ fontWeight: "bold", fontSize: "0.8rem" }} color="primary">
                    {titleEn}
                  </Typography> */}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{ gap: 3, flexGrow: 1, display: { xs: "none", md: "flex" } }}
            >
              {buildPages(userDetails.isAuthenticated, unreadNotificationCount, t, configLinks?.newsCatId).map((page) => (
                <Button
                  key={page.path}
                  onClick={() => onPageClick(page.path)}
                  sx={{ 
                    my: 2, 
                    color: "primary.main", 
                    display: "block",
                    position: "relative",
                    fontWeight: location.pathname === page.path ? "bold" : "normal",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      backgroundColor: "primary.main",
                      transform: location.pathname === page.path ? "scaleX(1)" : "scaleX(0)",
                      transition: "transform 0.3s ease",
                    },
                    "&:hover::after": {
                      transform: "scaleX(1)",
                    },
                  }}
                >
                  <Badge 
                    badgeContent={page.badgeContent} 
                    color="error" 
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: '18px', height: '18px' } }}
                  >
                    <Typography
                      variant={currentLang === 'ar' ? 'h6' : 'body1'}
                      sx={{ fontWeight: currentLang === 'ar' ? 'inherit' : 600 }}
                    >
                      {page.title}
                    </Typography>
                  </Badge>
                </Button>
              ))}
            </Box>
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "end",
                gap: 3,
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
              }}
            >
              {/* <Chip onClick={handleClick}
                              sx={{backgroundColor: "#F5F5F5"}}
                               size="large"
                              label={<Typography color="primary.main">English</Typography>}
                              variant="outlined" /> */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                  color="primary"
                  aria-label={t('nav_help')}
                  onClick={() => navigate("/help")}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: "primary.main",
                    backgroundColor: "#F5F5F5",
                    "&:hover": { backgroundColor: "rgba(188,31,65,0.08)" },
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
                {/* Language Toggle Button */}
                <Chip
                  onClick={toggleLanguage}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.5, color: "primary.main" }}>
                      {currentLang === 'ar' ? 'EN' : 'ع'}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: "#F5F5F5",
                    borderColor: "primary.main",
                    borderWidth: 1,
                    borderStyle: "solid",
                    height: 36,
                    borderRadius: 8,
                    px: 1,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(188,31,65,0.08)" },
                  }}
                />
                {userDetails.isAuthenticated ? (
                  <>
                    {/* User chip with avatar */}
                    <Chip
                      onClick={handleAvatarClick}
                      avatar={
                        <Avatar
                          src={
                            userDetails?.citizen?.profilePicturePreview ||
                            (userDetails?.citizen?.profilePicture ? `/attachments/${userDetails.citizen.profilePicture}` : undefined)
                          }
                          sx={{
                            width: 35,
                            height: 35,
                            bgcolor: getAvatarColor(
                              `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                            ),
                            color: "white !important",
                            fontWeight: "bold",
                            fontSize: "1.05rem",
                          }}
                        >
                          {!(userDetails?.citizen?.profilePicture || userDetails?.citizen?.profilePicturePreview) && getAvatarInitials(
                            `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                          )}
                        </Avatar>
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {userDetails?.citizen?.firstName} {userDetails?.citizen?.familyName}
                        </Typography>
                      }
                      sx={{ 
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        pr: 2,
                        pl: 0.5,
                        height: 42,
                        borderRadius: 8,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(191, 60, 78, 0.08)" },
                        "& .MuiChip-avatar": {
                          width: 35,
                          height: 35,
                          marginLeft: 0,
                          marginRight: 0,
                        },
                      }}
                    />
                    <UserAvatarMenu
                      anchorEl={avatarAnchorEl}
                      open={avatarMenuOpen}
                      onClose={handleAvatarClose}
                    />
                  </>
                ) : (
                  location.pathname !== "/login" && (
                    <Chip
                      onClick={() => navigate("/login")}
                      icon={<PersonIcon color="primary" />}
                      sx={{
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        height: 42,
                        borderRadius: 8,
                        px: 2,
                      }}
                      label={<Typography color="primary.main" sx={{ fontWeight: "500" }}>{t('login')}</Typography>}
                    />
                  )
                )}
                
                {/* Accessibility chip */}
                </Stack>
              <SettingsMenu
                anchorEl={anchorEl}
                open={open}
                handleClose={handleClose}
              />
            </Box>
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "end",
                gap: 1,
                flexGrow: 1,
                display: { xs: "flex", md: "none" },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  color="primary"
                  aria-label={t('nav_help')}
                  onClick={() => navigate("/help")}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: "primary.main",
                    backgroundColor: "#F5F5F5",
                    "&:hover": { backgroundColor: "rgba(188,31,65,0.08)" },
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
                {userDetails.isAuthenticated ? (
                  <>
                    {/* User chip with avatar - mobile */}
                    <Chip
                      onClick={handleAvatarClick}
                      avatar={
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: getAvatarColor(
                              `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                            ),
                            color: "white !important",
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                          }}
                        >
                          {getAvatarInitials(
                            `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                          )}
                        </Avatar>
                      }
                      label={
                        <Typography variant="caption" sx={{ fontWeight: "500", fontSize: "0.8rem" }}>
                          {userDetails?.citizen?.firstName}
                        </Typography>
                      }
                      sx={{ 
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        pr: 1.75,
                        pl: 0.5,
                        height: 36,
                        borderRadius: 8,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(191, 60, 78, 0.08)" },
                        "& .MuiChip-avatar": {
                          width: 30,
                          height: 30,
                          marginLeft: 0,
                          marginRight: 0,
                        },
                      }}
                    />
                    <UserAvatarMenu
                      anchorEl={avatarAnchorEl}
                      open={avatarMenuOpen}
                      onClose={handleAvatarClose}
                    />
                  </>
                ) : (
                  location.pathname !== "/login" && (
                    <Chip
                      onClick={() => navigate("/login")}
                      icon={<PersonIcon color="primary" sx={{ fontSize: 20 }} />}
                      sx={{
                        backgroundColor: "#F5F5F5",
                        border: "none",
                        height: 36,
                        borderRadius: 8,
                        px: 1.5,
                      }}
                      label={<Typography color="primary.main" sx={{ fontSize: "0.8rem", fontWeight: "500" }}>{t('login')}</Typography>}
                    />
                  )
                )}
                
               
              </Stack>
              <SettingsMenu
                anchorEl={anchorEl}
                open={open}
                handleClose={handleClose}
              />
            </Box>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
    
    {/* Mobile Nav Drawer */}
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        onOpen={() => setDrawerOpen(true)}
        swipeAreaWidth={37}
        disableSwipeToOpen={false}
        hysteresis={0.4}
        minFlingVelocity={350}
        sx={{
          "& .MuiDrawer-paper": {
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            width: "75%",
            maxWidth: 320,
            direction: currentLang === 'ar' ? "rtl" : "ltr",
            // Frosted glass effect with better text visibility
            background: "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(249,250,252,0.70) 100%)",
            backdropFilter: "blur(40px) saturate(200%)",
            WebkitBackdropFilter: "blur(40px) saturate(200%)",
            border: "1px solid rgba(255,255,255,0.4)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255,255,255,0.6)",
            color: "rgba(0,0,0,0.95)",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 2, py: 2.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }} color="primary">
                {t('more')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  color="primary"
                  aria-label={t('nav_help')}
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate("/help");
                  }}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: "primary.main",
                    backgroundColor: "#F5F5F5",
                    "&:hover": { backgroundColor: "rgba(188,31,65,0.08)" },
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Chip
                  onClick={toggleLanguage}
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {currentLang === 'ar' ? 'EN' : 'ع'}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: "#F5F5F5",
                    borderColor: "primary.main",
                    borderWidth: 1,
                    borderStyle: "solid",
                    height: 28,
                    borderRadius: 6,
                    px: 0.5,
                    cursor: "pointer",
                  }}
                />
                <IconButton onClick={handleDrawerToggle} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img height={75} src={logoSrc} alt="logo" />
              <Typography variant="body1" color="primary" sx={{ fontWeight: "bold", padding: 2}}>
                {title}
              </Typography>
            </Box>
          </Box>
          
          {/* User Section */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            {userDetails.isAuthenticated ? (
              <Box
                onClick={handleAvatarClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "rgba(191, 60, 78, 0.08)",
                  "&:hover": { backgroundColor: "rgba(191, 60, 78, 0.12)" },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(
                      `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                    ),
                    color: "white",
                    fontWeight: "bold",
                    width: 60,
                    height: 60,
                    fontSize: "1.3rem",
                  }}
                >
                  {getAvatarInitials(
                    `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`
                  )}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {userDetails?.citizen?.firstName} {userDetails?.citizen?.familyName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userDetails?.citizen?.documentNumber}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="contained"
                startIcon={<PersonIcon />}
                onClick={() => {
                  setDrawerOpen(false);
                  navigate("/login");
                }}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                {t('login')}
              </Button>
            )}
          </Box>
          
          {/* Navigation Items */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 0.5 }}>
            {buildPages(userDetails.isAuthenticated, unreadNotificationCount, t, configLinks?.newsCatId).map((page, index) => (
              <Button
                key={page.path}
                onClick={() => onPageClick(page.path)}
                fullWidth
                variant={location.pathname === page.path ? "outlined" : "text"}
                startIcon={page.icon}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "right",
                  direction: "rtl",
                  py: 1.5,
                  px: 2,
                  mb: 0.5,
                  borderRadius: 2,
                  color: location.pathname === page.path ? "primary.main" : "text.primary",
                  backgroundColor: location.pathname === page.path ? "rgba(191, 60, 78, 0.08)" : "transparent",
                  borderColor: location.pathname === page.path ? "primary.main" : "transparent",
                  borderWidth: location.pathname === page.path ? "2px" : "1px",
                  "&:hover": {
                    backgroundColor: "rgba(191, 60, 78, 0.12)",
                    borderColor: location.pathname === page.path ? "primary.main" : "transparent",
                  },
                  "& .MuiButton-startIcon": {
                    marginLeft: "8px",
                    marginRight: "8px",
                  },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: location.pathname === page.path ? "bold" : "normal" }}>
                  {page.title}
                </Typography>
              </Button>
            ))}
          </Box>
        </Box>
  </SwipeableDrawer>

      {/* WhatsApp FAB removed */}

      <SettingsMenu
        anchorEl={anchorEl}
        open={open}
        handleClose={handleClose}
      />
    </Box>
    </>
  );
}

export default ResponsiveNavBar;



